import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import ooeConstants from '../constants';
import { logDatadogError } from '../services/datadog';
import { addressErrorMessages } from '../util/customerErrorMessages';
import { AddressResult } from './schemas';
import { autocompleteAddress, validateAddress } from './thunks';

export type State = {
  loading: {
    addressSearch: boolean;
    addressValidation: boolean;
  };
  error: {
    addressSearch: Nullable<string>;
    addressValidation: Nullable<string>;
  };
  addressSearchValue: string;
  addressSearchResults: AddressResult[];
  addressSearchOpen: boolean;
  addressSearchRequestId: Nullable<string>;
  addressValidationRequestId: Nullable<string>;
  addressSearchBlurred: boolean;
  addressSearchSelectedPlaceId: Nullable<string>;
  addressSearchSeededFromEdit: boolean;
};

export const initialState: State = {
  loading: {
    addressSearch: false,
    addressValidation: false,
  },
  error: {
    addressSearch: null,
    addressValidation: null,
  },
  addressSearchValue: '',
  addressSearchResults: [],
  addressSearchOpen: false,
  addressSearchRequestId: null,
  addressValidationRequestId: null,
  addressSearchBlurred: false,
  addressSearchSelectedPlaceId: null,
  addressSearchSeededFromEdit: false,
};

const slice = createSlice({
  name: 'eventDetails',
  initialState,
  reducers: {
    setAddressSearchValue: (state, action: PayloadAction<{ value: string; autocomplete?: boolean }>) => {
      const { value } = action.payload;
      state.addressSearchSeededFromEdit = false;
      state.addressSearchValue = value;
      state.addressSearchBlurred = false;
      state.addressSearchSelectedPlaceId = null;
      state.error.addressValidation = null;
      state.loading.addressValidation = false;

      if (value.trim().length >= ooeConstants.ADDRESS_SEARCH_MIN_TYPEAHEAD_LENGTH) {
        return;
      }

      state.addressSearchResults = [];
      state.addressSearchOpen = false;
      state.error.addressSearch = null;
    },
    closeAddressSearch: (state) => {
      state.addressSearchOpen = false;
    },
    blurAddressSearch: (state) => {
      state.addressSearchBlurred = true;
    },
    clearAddressSearch: () => initialState,
    setAddressValidationError: (state, action: PayloadAction<Nullable<string>>) => {
      state.error.addressValidation = action.payload;
    },
    seedAddressSearchFromEdit: (state, action: PayloadAction<{ value: string; placeId?: string | null }>) => {
      const { value, placeId } = action.payload;
      state.addressSearchSeededFromEdit = true;
      state.addressSearchValue = value;
      state.addressSearchSelectedPlaceId = placeId || 'seeded';
      state.addressSearchBlurred = false;
      state.addressSearchOpen = false;
      state.error.addressSearch = null;
      state.error.addressValidation = null;
      state.loading.addressValidation = false;
    },
    selectAddressSearchResult: (state, action: PayloadAction<AddressResult>) => {
      const { placeId, structuredFormatting } = action.payload;
      const { mainText, secondaryText } = structuredFormatting;
      state.addressSearchValue = [mainText, secondaryText].filter(Boolean).join(', ');
      state.addressSearchSelectedPlaceId = placeId;
      state.addressSearchOpen = false;
      state.addressSearchBlurred = false;
      state.error.addressSearch = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(autocompleteAddress.pending, (state, action) => {
        state.addressSearchRequestId = action.meta.requestId;
        state.loading.addressSearch = true;
        state.error.addressSearch = null;
        state.addressSearchOpen = true;
        state.addressSearchResults = [];
      })
      .addCase(autocompleteAddress.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.addressSearchRequestId) {
          return;
        }

        state.addressSearchResults = action.payload.results;
        state.loading.addressSearch = false;
        state.error.addressSearch = null;
        state.addressSearchOpen = true;
      })
      .addCase(autocompleteAddress.rejected, (state, action) => {
        if (action.meta.aborted) {
          return;
        }
        if (action.meta.requestId !== state.addressSearchRequestId) {
          return;
        }

        const { error } = action;
        logDatadogError('Autocomplete Address Error', { eventDetails: state }, error);
        state.loading.addressSearch = false;
        state.addressSearchResults = [];
        state.addressSearchOpen = true;
        state.error.addressSearch = error.message ?? null;
      })
      .addCase(validateAddress.pending, (state, action) => {
        state.addressValidationRequestId = action.meta.requestId;
        state.loading.addressValidation = true;
        state.error.addressValidation = null;
      })
      .addCase(validateAddress.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.addressValidationRequestId) {
          return;
        }

        state.loading.addressValidation = false;
        state.error.addressValidation = null;
      })
      .addCase(validateAddress.rejected, (state, action) => {
        if (action.meta.requestId !== state.addressValidationRequestId) {
          return;
        }
        if (action.meta.aborted) {
          state.loading.addressValidation = false;
          return;
        }

        const { error } = action;
        logDatadogError('Validate Address Error', { eventDetails: state }, error);
        state.loading.addressValidation = false;
        state.error.addressValidation = addressErrorMessages.ERR_NOT_DELIVERABLE.message;
      });
  },
});

export const actions = slice.actions;
export default slice.reducer;
